import { getFirestore, doc, collection, query, where, orderBy, limit, addDoc, getDoc, getDocs, setDoc, onSnapshot, deleteDoc, getCountFromServer } from "firebase/firestore"
let db = getFirestore()

let collectionName = "profiles"

function docToInstance(document) {
	let data = document.data()
	if (!data) return null

	let activeDiscounts = data.activeDiscounts || []
	if (!activeDiscounts.length && data.activeDiscount && data.activeDiscount.percentage) {
		activeDiscounts = [{ id: 'legacy', ...data.activeDiscount }]
	}

	return new Profile(
		document.id,
		data.name,
		data.email,
		data.phone,
		data.role,
		data.permissions,
		data.activated,
		data.cash,
		data.collections,
		data.boosters,
		data.cards,
		data.codes,
		data.achievements,
		data.stats,
		data.lastLogin,
		data.lastWheelSpin,
		data.oldCodesVerified,
		data.oldCodeRefused,
		data.helpSeen,
		data.souls,
		data.casinoCost,
		data.lastKnownVersion,
		activeDiscounts,
		data.auctionPoints,
		data.luckBoost || null,
		data.tradeTickets || 0,
		data.treasureRoom || null
	)
}

class Profile {
	constructor(id, name, email, phone, role, permissions, activated, cash, collections, boosters, cards, codes, achievements, stats, lastLogin, lastWheelSpin, oldCodesVerified, oldCodeRefused, helpSeen, souls, casinoCost, lastKnownVersion, activeDiscounts = [], auctionPoints = 0, luckBoost = null, tradeTickets = 0, treasureRoom = null) {
		this.id = id
		this.name = name
		this.email = email
		this.phone = phone
		this.role = role
		this.permissions = permissions
		this.activated = activated
		this.cash = cash
		this.collections = collections
		this.boosters = boosters
		this.cards = cards
		this.codes = codes
		this.achievements = achievements
		this.stats = stats
		this.lastLogin = lastLogin
		this.lastWheelSpin = lastWheelSpin
		this.oldCodesVerified = oldCodesVerified
		this.oldCodeRefused = oldCodeRefused
		this.helpSeen = helpSeen
		this.souls = souls
		this.casinoCost = casinoCost
		this.lastKnownVersion = lastKnownVersion
		this.activeDiscounts = activeDiscounts || []
		this.auctionPoints = auctionPoints || 0
		this.luckBoost = luckBoost || null
		this.tradeTickets = tradeTickets || 0
		this.treasureRoom = treasureRoom || null

		if (this.casinoCost === undefined) {
			this.casinoCost = 50
		}

		if (this.souls === undefined) {
			this.souls = 0
		}

		if (!this.stats) {
			this.stats = {
				public: true,
				open: 0,
				openByCollection: {},
				destroy: 0,
				upgrades: 0,
				downgrades: 0,
				trades: 0,
				maxTradeValue: 0,
				codeUsed: 0,
			}
		} else {
			if (this.stats.public === undefined) { this.stats.public = true }
			if (this.stats.open === undefined) { this.stats.open = 0 }
			if (!this.stats.openByCollection) { this.stats.openByCollection = {} }
			if (this.stats.destroy === undefined) { this.stats.destroy = 0 }
			if (this.stats.upgrades === undefined) { this.stats.upgrades = 0 }
			if (this.stats.downgrades === undefined) { this.stats.downgrades = 0 }
			if (this.stats.trades === undefined) { this.stats.trades = 0 }
			if (this.stats.maxTradeValue === undefined) { this.stats.maxTradeValue = 0 }
			if (this.stats.codeUsed === undefined) { this.stats.codeUsed = 0 }
		}

		if (!this.lastWheelSpin) {
			this.lastWheelSpin = 0
		}

		if (typeof this.lastLogin !== 'number') {
			this.lastLogin = Date.now()
		}
	}

	static initOne(uid, name, email, phone, cash = 0) {
		let stats = {
			public: true,
			open: 0,
			openByCollection: {},
			destroy: 0,
			upgrades: 0,
			downgrades: 0,
			trades: 0,
			maxTradeValue: 0,
			codeUsed: 0,
		}
		const newProfile = new Profile(uid, name, email, phone, "User", [], false, cash, {}, {}, {}, {}, {}, stats, 0, 0, null, false, false, 0, 50, null)
		return newProfile
	}

	static async getByPage(page, pageSize, filters = {}) {
		if (filters.name && filters.name.trim()) {
			const search = filters.name.trim().toLowerCase()
			const all = (await getDocs(query(collection(db, collectionName), orderBy("name", "asc")))).docs.map(docToInstance)
			const filtered = all.filter(p => p && p.name && p.name.toLowerCase().includes(search))
			const startIndex = (page - 1) * pageSize
			return filtered.slice(startIndex, startIndex + pageSize)
		}

		const documents = await getDocs(query(collection(db, collectionName), orderBy("name", "asc"), limit(page * pageSize)))
		const allDocs = documents.docs.map(docToInstance)
		const startIndex = (page - 1) * pageSize
		return allDocs.slice(startIndex, startIndex + pageSize)
	}

	static async getCount(filters = {}) {
		if (filters.name && filters.name.trim()) {
			const search = filters.name.trim().toLowerCase()
			const all = (await getDocs(collection(db, collectionName))).docs.map(docToInstance)
			return all.filter(p => p && p.name && p.name.toLowerCase().includes(search)).length
		}

		const snapshot = await getCountFromServer(query(collection(db, collectionName)))
		return snapshot.data().count
	}

	static async getAll() {
		const documents = await getDocs(collection(db, collectionName))
		return documents.docs.map(docToInstance)
	}

	static async getById(id) {
		const document = await getDoc(doc(db, collectionName, id))
		return docToInstance(document)
	}

	static async getByName(name) {
		const q = query(collection(db, collectionName), where("name", "==", name))
		const documents = await getDocs(q)
		return documents.docs.map(docToInstance)
	}

	static async getByEmail(email) {
		const q = query(collection(db, collectionName), where("email", "==", email))
		const documents = await getDocs(q)
		return documents.docs.map(docToInstance)
	}

	static async getByActivated(activated) {
		const q = query(collection(db, collectionName), where("activated", "==", activated))
		const documents = await getDocs(q)
		return documents.docs.map(docToInstance)
	}

	static async listenAll(callback) {
		return onSnapshot(collection(db, collectionName), (snapshot) => {
			const list = []
			snapshot.forEach((doc) => {
				list.push(docToInstance(doc))
			})
			callback(list)
		})
	}

	static async listenById(id, callback) {
		return onSnapshot(doc(db, collectionName, id), (snapshot) => {
			callback(docToInstance(snapshot))
		})
	}

	static async listenByName(name, callback) {
		const q = query(collection(db, collectionName), where("name", "==", name))
		return onSnapshot(q, (snapshot) => {
			const list = []
			snapshot.forEach((doc) => {
				list.push(docToInstance(doc))
			})
			callback(list)
		})
	}

	static async listenByEmail(email, callback) {
		const q = query(collection(db, collectionName), where("email", "==", email))
		return onSnapshot(q, (snapshot) => {
			const list = []
			snapshot.forEach((doc) => {
				list.push(docToInstance(doc))
			})
			callback(list)
		})
	}

	static async listenByActivated(activated, callback) {
		const q = query(collection(db, collectionName), where("activated", "==", activated))
		return onSnapshot(q, (snapshot) => {
			const list = []
			snapshot.forEach((doc) => {
				list.push(docToInstance(doc))
			})
			callback(list)
		})
	}

	async save() {
		const new_doc = {
			name: this.name,
			email: this.email,
			phone: this.phone,
			role: this.role,
			permissions: this.permissions,
			activated: this.activated,
			cash: this.cash,
			collections: this.collections,
			boosters: this.boosters,
			cards: this.cards,
			codes: this.codes,
			achievements: this.achievements,
			stats: this.stats,
			lastLogin: this.lastLogin,
			lastWheelSpin: this.lastWheelSpin,
			oldCodesVerified: this.oldCodesVerified || null,
			oldCodeRefused: this.oldCodeRefused || false,
			helpSeen: this.helpSeen || false,
			souls: this.souls || 0,
			casinoCost: this.casinoCost || 50,
			lastKnownVersion: this.lastKnownVersion || null,
			activeDiscounts: this.activeDiscounts || [],
			auctionPoints: this.auctionPoints || 0,
			luckBoost: this.luckBoost || null,
			tradeTickets: this.tradeTickets || 0,
			treasureRoom: this.treasureRoom || null,
		}

		if (this.id) {
			await setDoc(doc(db, collectionName, this.id), new_doc)
		} else {
			const docRef = await addDoc(collection(db, collectionName), new_doc)
			this.id = docRef.id
		}
		return true
	}

	async delete() {
		if (this.id) {
			await deleteDoc(doc(db, collectionName, this.id))
		}
		return true
	}
}

export default Profile
